// React
import { useEffect, useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import PropTypes from "prop-types";
import { useTranslation } from "react-i18next";

// Components
import { DataTable, FieldSearchSelect } from "components/AtomsV2";
import Icon from "components/ShellV2/Icon";

// Redux
import { useGetTariffsByValidityQuery } from "state/erse/tariffsApiSlice";

// constants
import { getERSETypeLabelFromValue, getPeriodLabelFromValue } from "utils/constants/helpers";

function downloadTariffsCSV(data, validity) {
  const headers = [
    "t",
    "type",
    "ic_voltage_level",
    "ipr_voltage_level",
    "trimester",
    "period",
    "cieg_discount",
    "value",
  ];
  const csvRows = data.map((tariff) => [
    validity,
    tariff.type,
    tariff.ic_voltage_level,
    tariff.ipr_voltage_level ?? "",
    tariff.trimester ?? "",
    tariff.period ?? "",
    tariff.cieg_discount ?? "",
    tariff.value,
  ]);
  const csv = [headers, ...csvRows].map((r) => r.join(",")).join("\n");
  const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = `tarifas_${validity}.csv`;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}

const emptyValue = "-";

const formatPercentage = (value) => (value != null ? `${value}%` : emptyValue);

const formatTrimester = (value) => (value != null ? value : emptyValue);

function formatTariffValue(value, tariff) {
  if (value == null) return emptyValue;

  const numericValue = Number(value);
  if (Number.isNaN(numericValue)) return value;

  const formatted = numericValue.toFixed(4);
  if (tariff.type_code === "ENERGY") return `${formatted} €/kWh`;
  if (tariff.type_code === "POWER") return `${formatted} €/kW/dia`;
  if (tariff.type_code === "CONTRACTED_POWER") {
    return tariff.ic_voltage_level?.includes("BTN")
      ? `${formatted} €/dia`
      : `${formatted} €/kW.dia`;
  }

  return formatted;
}

function TariffsTable({ validity: validityProp }) {
  const { validity: routeValidity } = useParams();
  const validity = validityProp ?? routeValidity;

  const { t } = useTranslation(["erse"]);
  const [type, setType] = useState("");
  const [icVoltageLevel, setIcVoltageLevel] = useState("");
  const [period, setPeriod] = useState("");

  // READ tariffs
  const { data } = useGetTariffsByValidityQuery(validity, { skip: !validity });

  const rows = useMemo(
    () =>
      (data ?? []).map((tariff, index) => {
        const ciegDiscount =
          tariff.cieg_discount != null && tariff.cieg_discount !== ""
            ? Number(tariff.cieg_discount)
            : null;
        const trimester = Number(tariff.trimester) > 0 ? Number(tariff.trimester) : null;

        return {
          id: `${tariff.type}-${tariff.ic_voltage_level}-${tariff.period ?? "no-period"}-${index}`,
          type: getERSETypeLabelFromValue(tariff.type),
          type_code: tariff.type,
          ic_voltage_level: tariff.ic_voltage_level,
          ipr_voltage_level: tariff.ipr_voltage_level || emptyValue,
          trimester,
          period: tariff.period ? getPeriodLabelFromValue(tariff.period) : emptyValue,
          period_code: tariff.period ?? "",
          cieg_discount: Number.isNaN(ciegDiscount) ? null : ciegDiscount,
          value: tariff.value,
        };
      }),
    [data]
  );

  useEffect(() => {
    setType("");
    setIcVoltageLevel("");
    setPeriod("");
  }, [validity]);

  const filteredRows = useMemo(
    () =>
      rows.filter(
        (row) =>
          (!type || row.type_code === type) &&
          (!icVoltageLevel || row.ic_voltage_level === icVoltageLevel) &&
          (!period || row.period_code === period)
      ),
    [rows, type, icVoltageLevel, period]
  );

  const typeOptions = useMemo(() => {
    const options = new Map();
    rows.forEach((row) => options.set(row.type_code, row.type));
    return Array.from(options, ([value, label]) => ({ value, label })).sort((a, b) =>
      a.label.localeCompare(b.label)
    );
  }, [rows]);

  const icVoltageLevelOptions = useMemo(
    () =>
      Array.from(new Set(rows.map((row) => row.ic_voltage_level)))
        .filter(Boolean)
        .sort((a, b) => a.localeCompare(b, undefined, { numeric: true }))
        .map((value) => ({ value, label: value })),
    [rows]
  );

  const periodOptions = useMemo(() => {
    const options = new Map();
    rows.forEach((row) => {
      if (row.period_code) options.set(row.period_code, row.period);
    });
    return Array.from(options, ([value, label]) => ({ value, label })).sort((a, b) =>
      a.label.localeCompare(b.label, undefined, { numeric: true })
    );
  }, [rows]);

  const columns = [
    {
      key: "type",
      label: t("tables.tariffs.columns.type"),
      width: "16%",
      render: (value) => <span className="font-medium text-ink">{value}</span>,
    },
    {
      key: "ic_voltage_level",
      label: t("tables.tariffs.columns.ic_voltage_level"),
      width: "14%",
    },
    {
      key: "ipr_voltage_level",
      label: t("tables.tariffs.columns.ipr_voltage_level"),
      width: "14%",
    },
    {
      key: "trimester",
      label: t("tables.tariffs.columns.trimester"),
      align: "center",
      width: "10%",
      render: formatTrimester,
    },
    {
      key: "period",
      label: t("tables.tariffs.columns.period"),
      width: "12%",
    },
    {
      key: "cieg_discount",
      label: t("tables.tariffs.columns.cieg_discount"),
      align: "right",
      width: "12%",
      render: formatPercentage,
    },
    {
      key: "value",
      label: t("tables.tariffs.columns.value"),
      align: "right",
      render: (value, row) => (
        <span className="tabular-nums" style={{ color: "var(--accent)", fontWeight: 600 }}>
          {formatTariffValue(value, row)}
        </span>
      ),
    },
  ];

  const toolbarActions = (
    <>
      <FieldSearchSelect
        label={t("tables.tariffs.columns.type")}
        value={type}
        onChange={setType}
        options={[{ value: "", label: t("tables.tariffs.filters.all_types") }, ...typeOptions]}
      />
      <FieldSearchSelect
        label={t("tables.tariffs.columns.ic_voltage_level")}
        value={icVoltageLevel}
        onChange={setIcVoltageLevel}
        options={[
          { value: "", label: t("tables.tariffs.filters.all_ic_levels") },
          ...icVoltageLevelOptions,
        ]}
      />
      <FieldSearchSelect
        label={t("tables.tariffs.columns.period")}
        value={period}
        onChange={setPeriod}
        options={[{ value: "", label: t("tables.tariffs.filters.all_periods") }, ...periodOptions]}
      />
      <button
        type="button"
        className="rec-btn-ghost rec-btn-sm"
        onClick={() => data && downloadTariffsCSV(data, validity)}
        disabled={!data || data.length === 0}
      >
        <Icon name="download" style={{ fontSize: 16 }} />
        {t("tables.tariffs.export_csv")}
      </button>
    </>
  );

  return (
    <DataTable
      columns={columns}
      rows={filteredRows}
      maxRows={10}
      search={t("tables.tariffs.search")}
      searchKeys={["type", "ic_voltage_level", "ipr_voltage_level", "period"]}
      filters={toolbarActions}
      pageResetKey={`${type}|${icVoltageLevel}|${period}`}
      emptyText={t("list.empty")}
    />
  );
}

TariffsTable.propTypes = {
  validity: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
};

TariffsTable.defaultProps = {
  validity: null,
};

export default TariffsTable;
